/**
 * BadgeExamples.tsx
 * Comprehensive examples of HeroUI Badge component
 * 
 * Showcases all HeroUI Badge features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Bell, Mail, ShoppingCart, User } from 'lucide-react';


export const BadgeExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="flex items-center gap-6">
                <Badge content="5">
                  <Button variant="bordered" color="primary">
                    <Bell className="w-4 h-4" />
                    Notifications
                  </Button>
                </Badge>
                
                <Badge content="99+">
                  <Button variant="bordered" color="primary">
                    <Mail className="w-4 h-4" />
                    Messages
                  </Button>
                </Badge>
                
                <Badge content="3">
                  <Button variant="bordered" color="primary">
                    <ShoppingCart className="w-4 h-4" />
                    Cart
                  </Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge Colors</h3>
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-4">
                <Badge content="1" color="default">
                  <Button variant="bordered" color="primary">Default</Button>
                </Badge>
                <Badge content="2" color="primary">
                  <Button variant="bordered" color="primary">Primary</Button>
                </Badge>
                <Badge content="3" color="secondary">
                  <Button variant="bordered" color="primary">Secondary</Button>
                </Badge>
                <Badge content="4" color="success">
                  <Button variant="bordered" color="primary">Success</Button>
                </Badge>
                <Badge content="5" color="warning">
                  <Button variant="bordered" color="primary">Warning</Button>
                </Badge>
                <Badge content="6" color="danger">
                  <Button variant="bordered" color="primary">Danger</Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge Sizes</h3>
          <Card>
            <CardBody>
              <div className="flex items-center gap-6">
                <Badge content="1" size="sm">
                  <Button variant="bordered" color="primary" size="sm">Small Badge</Button>
                </Badge>
                <Badge content="12" size="md">
                  <Button variant="bordered" color="primary" size="md">Medium Badge</Button>
                </Badge>
                <Badge content="123" size="lg">
                  <Button variant="bordered" color="primary" size="lg">Large Badge</Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge Variants</h3>
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-6">
                <Badge content="5" variant="solid">
                  <Button variant="bordered" color="primary">Solid</Button>
                </Badge>
                <Badge content="5" variant="faded">
                  <Button variant="bordered" color="primary">Faded</Button>
                </Badge>
                <Badge content="5" variant="flat">
                  <Button variant="bordered" color="primary">Flat</Button>
                </Badge>
                <Badge content="5" variant="faded">
                  <Button variant="bordered" color="primary">Faded</Button>
                </Badge>
                <Badge content="5" variant="shadow">
                  <Button variant="bordered" color="primary">Shadow</Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge Shapes</h3>
          <Card>
            <CardBody>
              <div className="flex items-center gap-6">
                <Badge content="1" shape="rectangle">
                  <Button variant="bordered" color="primary">Rectangle</Button>
                </Badge>
                <Badge content="5" shape="circle">
                  <Button variant="bordered" color="primary">Circle</Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge Placement</h3>
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-sm text-text-secondary mb-2">Top Right</p>
                  <Badge content="1" placement="top-right">
                    <Button variant="bordered" color="primary" className="p-2">
                      <User className="w-4 h-4" />
                    </Button>
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-sm text-text-secondary mb-2">Top Left</p>
                  <Badge content="2" placement="top-left">
                    <Button variant="bordered" color="primary" className="p-2">
                      <User className="w-4 h-4" />
                    </Button>
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-sm text-text-secondary mb-2">Bottom Right</p>
                  <Badge content="3" placement="bottom-right">
                    <Button variant="bordered" color="primary" className="p-2">
                      <User className="w-4 h-4" />
                    </Button>
                  </Badge>
                </div>
                <div className="text-center">
                  <p className="text-sm text-text-secondary mb-2">Bottom Left</p>
                  <Badge content="4" placement="bottom-left">
                    <Button variant="bordered" color="primary" className="p-2">
                      <User className="w-4 h-4" />
                    </Button>
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Invisible Badge (Dot Indicator)</h3>
          <Card>
            <CardBody>
              <div className="flex items-center gap-6">
                <Badge content="" color="danger" variant="solid">
                  <Button variant="bordered" color="primary">
                    <Bell className="w-4 h-4" />
                    Has Notifications
                  </Button>
                </Badge>
                
                <Badge content="" color="success" variant="solid">
                  <Button variant="bordered" color="primary">
                    <User className="w-4 h-4" />
                    Online Status
                  </Button>
                </Badge>
                
                <Badge content="" color="warning" variant="solid">
                  <Button variant="bordered" color="primary">
                    <Mail className="w-4 h-4" />
                    Pending
                  </Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Badge with Different Components</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Badge on Chip</p>
                  <Badge content="new" color="danger" size="sm">
                    <Chip color="primary">Latest Feature</Chip>
                  </Badge>
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary mb-2">Badge on Text</p>
                  <Badge content="5" color="primary">
                    <span className="px-3 py-2 bg-surface-elevated rounded-lg text-text-primary">
                      Inbox
                    </span>
                  </Badge>
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary mb-2">Badge on Avatar/Icon</p>
                  <Badge content="99+" color="danger">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-text-inverse" />
                    </div>
                  </Badge>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Content</h3>
          <Card>
            <CardBody>
              <div className="flex items-center gap-6">
                <Badge content="NEW" color="danger">
                  <Button variant="bordered" color="primary">Text Badge</Button>
                </Badge>
                
                <Badge content="!" color="warning">
                  <Button variant="bordered" color="primary">Alert Badge</Button>
                </Badge>
                
                <Badge content="✓" color="success">
                  <Button variant="bordered" color="primary">Check Badge</Button>
                </Badge>
                
                <Badge content="●" color="primary">
                  <Button variant="bordered" color="primary">Dot Badge</Button>
                </Badge>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Use Cases</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Trade Notifications</p>
                  <div className="flex items-center gap-4">
                    <Badge content="3" color="success">
                      <Button variant="bordered" color="primary">
                        Winning Trades
                      </Button>
                    </Badge>
                    <Badge content="1" color="danger">
                      <Button variant="bordered" color="primary">
                        Losing Trades
                      </Button>
                    </Badge>
                    <Badge content="2" color="warning">
                      <Button variant="bordered" color="primary">
                        Pending Orders
                      </Button>
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary mb-2">Alert Status</p>
                  <div className="flex items-center gap-4">
                    <Badge content="" color="danger" variant="solid">
                      <Button variant="bordered" color="primary">
                        Risk Alert
                      </Button>
                    </Badge>
                    <Badge content="" color="success" variant="solid">
                      <Button variant="bordered" color="primary">
                        Target Hit
                      </Button>
                    </Badge>
                    <Badge content="" color="primary" variant="solid">
                      <Button variant="bordered" color="primary">
                        Price Alert
                      </Button>
                    </Badge>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};